(() => {
  'use strict';

  const READY_FLAG = 'kbDrawerReady';
  // The block can be duplicated, so the script may be requested more than once.
  if (document.documentElement.dataset[READY_FLAG]) return;
  document.documentElement.dataset[READY_FLAG] = 'true';

  const OPEN_SELECTOR = '[data-kb-drawer-open]';
  const CLOSE_SELECTOR = '[data-kb-drawer-close]';
  const DRAWER_SELECTOR = '[data-kb-drawer]';
  const CLOSE_BUTTON_SELECTOR = '.kalaboost-tab-drawer__close';
  const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const OPEN_CLASS = 'is-open';
  const LOCK_CLASS = 'kb-drawer-open';
  const TRANSITION_MS = 300;

  const homes = new WeakMap();

  let openDrawer = null;
  let openTrigger = null;
  let lastFocus = null;
  let closeTimer = null;
  let pending = null;

  function focusablesIn(drawer) {
    return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null
    );
  }

  // Runs once the slide-out animation is over, or right away when another
  // drawer takes over: a drawer left on the body would swallow every click.
  function finalizePending() {
    window.clearTimeout(closeTimer);
    if (!pending) return;

    const { drawer, home } = pending;
    pending = null;
    drawer.setAttribute('hidden', '');
    if (home && home.isConnected) home.appendChild(drawer);
    else drawer.remove();
  }

  function open(drawer, trigger) {
    if (!drawer || openDrawer === drawer) return;
    if (openDrawer) close({ restoreFocus: false });
    finalizePending();

    if (!homes.has(drawer)) homes.set(drawer, drawer.parentElement);

    lastFocus = document.activeElement;
    openDrawer = drawer;
    openTrigger = trigger || null;

    // The product column is sticky, which traps fixed children below the
    // header: the drawer only paints on top once it hangs off the body.
    document.body.appendChild(drawer);
    drawer.removeAttribute('hidden');
    document.documentElement.classList.add(LOCK_CLASS);
    openTrigger?.setAttribute('aria-expanded', 'true');

    // Let the browser paint the closed state before animating to the open one.
    requestAnimationFrame(() => drawer.classList.add(OPEN_CLASS));

    drawer.querySelector(CLOSE_BUTTON_SELECTOR)?.focus();
  }

  function close({ restoreFocus = true } = {}) {
    const drawer = openDrawer;
    if (!drawer) return;

    const home = homes.get(drawer);
    const trigger = openTrigger;
    const focusTarget = restoreFocus ? trigger || lastFocus : null;

    drawer.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove(LOCK_CLASS);
    trigger?.setAttribute('aria-expanded', 'false');

    openDrawer = null;
    openTrigger = null;
    lastFocus = null;

    finalizePending();
    pending = { drawer, home };
    closeTimer = window.setTimeout(finalizePending, TRANSITION_MS);

    if (focusTarget && typeof focusTarget.focus === 'function' && focusTarget.isConnected) {
      focusTarget.focus();
    }
  }

  function trapFocus(event) {
    const focusables = focusablesIn(openDrawer);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(OPEN_SELECTOR);
    if (trigger) {
      event.preventDefault();
      const id = trigger.getAttribute('data-kb-drawer-open');
      const drawer =
        (id && document.getElementById(id)) || trigger.parentElement?.querySelector(DRAWER_SELECTOR);
      open(drawer, trigger);
      return;
    }

    if (openDrawer && event.target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!openDrawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Tab') trapFocus(event);
  });

  document.addEventListener('shopify:section:unload', () => {
    if (openDrawer) close({ restoreFocus: false });
  });
})();
